import * as Sentry from "@sentry/react"
import { useEffect } from "react"

/**
 * Error Boundary component that catches and reports errors to Sentry.
 * Uses Sentry's ErrorBoundary with a fallback that receives the error from
 * Sentry only (no useRouteError), so it works with BrowserRouter.
 *
 * Usage: Wrap app content as <ErrorBoundary>{children}</ErrorBoundary>
 */
function ErrorFallback({
  error,
  componentStack,
  resetError,
}: {
  error: unknown
  componentStack: string
  eventId: string
  resetError: () => void
}) {
  useEffect(() => {
    if (error instanceof Error) {
      Sentry.captureException(error)
    }
  }, [error])

  let errorMessage = "An unexpected error occurred"
  let errorDetails = ""

  if (error instanceof Error) {
    errorMessage = error.message
    errorDetails = error.stack || ""
  } else if (typeof error === "string") {
    errorMessage = error
  } else {
    errorMessage = String(error)
  }
  if (componentStack) {
    errorDetails = errorDetails ? `${errorDetails}\n\n${componentStack}` : componentStack
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md space-y-4 rounded-lg border border-gray-200 bg-white p-8 shadow-lg">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-gray-600">{errorMessage}</p>
        </div>

        {import.meta.env.DEV && errorDetails && (
          <details className="mt-4 rounded-md bg-gray-50 p-4 text-sm">
            <summary className="cursor-pointer font-medium text-gray-700">
              Error details (development only)
            </summary>
            <pre className="mt-2 overflow-auto whitespace-pre-wrap text-xs text-gray-600">
              {errorDetails}
            </pre>
          </details>
        )}

        <div className="mt-6 flex gap-3">
          <button
            onClick={resetError}
            className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Reload page
          </button>
          <button
            onClick={() => (window.location.href = "/")}
            className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Go home
          </button>
        </div>

        <p className="mt-4 text-center text-xs text-gray-500">
          This error has been automatically reported. If the issue persists,
          please contact support.
        </p>
      </div>
    </div>
  )
}

export const ErrorBoundary = Sentry.withErrorBoundary(
  function ErrorBoundaryContent({ children }: { children: React.ReactNode }) {
    return <>{children}</>
  },
  {
    fallback: (props) => <ErrorFallback {...props} />,
    showDialog: false,
  }
)
